import { useEffect, useMemo, useState } from "react"
import {
  CalendarHubClient,
  CourseClassCalendarDto,
  MsPrivateCalendarDto,
} from "@clients/calendarHubClient"
import { useAppSelector } from "@app/hooks"
import { selectLoggedInUser } from "@features/login/loginSelectors"
import { useEventActions } from "../../main/calendar/useEventActions"
import Stack from "@mui/material/Stack"
import MsCalendar from "@masterschool/ui-calendar"
import {
  CalendarFilters,
  CalendarGroupings,
} from "@masterschool/ui-calendar/dist/src/types"
import { Typography } from "@mui/material"
import {
  buildMsCalendarGroupingsAndFiltersForProgramOwner,
  mapToMsCalendarProps,
} from "../../main/calendar/calendarMappers"
import {
  useGetCalendarsForProgramOwner,
  useLazyPastPrograms,
} from "../../main/calendar/useGetCalendar"
import { FetchEventSnackbar } from "../../main/calendar/FetchEventSnackbar"
import appTheme from "../../theme/appTheme"

import { UsersClient } from "@clients/usersClient"
import { Role } from "@features/login/loginSlice"
import { selectCourseInstanceClasses } from "@features/courseInstance/courseInstanceSelectors"
import { selectPrograms } from "@features/program/programSliceSelectors"
import { DateRange, defaultDateRange } from "@utils/date"

export const CourseInstanceCalendar = (props: {
  courseInstanceId: string | undefined
}) => {
  const { courseInstanceId } = props
  const [dateRange, setDateRange] = useState<DateRange>(defaultDateRange)
  const user = useAppSelector(selectLoggedInUser)
  const courseClasses = useAppSelector((state) =>
    selectCourseInstanceClasses(state, courseInstanceId),
  )

  const [classCalendars, setClassCalendars] = useState<
    CourseClassCalendarDto[]
  >([])
  const [staffCalendars, setStaffCalendars] = useState<MsPrivateCalendarDto[]>(
    [],
  )
  // When on, drops the single-course scope and shows ALL the user's programs
  // (reusing the main calendar's data path) so they can invite across programs.
  const [showAllPrograms, setShowAllPrograms] = useState(false)
  const { calendars: allProgramsCalendars } =
    useGetCalendarsForProgramOwner(showAllPrograms)
  const programs = useAppSelector(selectPrograms)
  const isAdmin =
    user?.roles.some((r) =>
      [
        Role.SchoolMasterAdmin,
        Role.LmsAdmin,
        Role.MasterschoolEmployee,
      ].includes(r),
    ) ?? false
  // Past programs only surface once the all-programs view is on.
  const lazyPastPrograms = useLazyPastPrograms(showAllPrograms && isAdmin)

  useEffect(() => {
    if (!courseClasses) {
      return
    }
    CalendarHubClient.getCourseClassesCalendar(
      courseClasses.map((c) => c.id),
    ).then((calendars) => {
      setClassCalendars(calendars)
    })
  }, [courseClasses])

  useEffect(() => {
    UsersClient.getUsersWithRoles([
      Role.Mentor,
      Role.Instructor,
      Role.ProgramManager,
      Role.CourseManager,
    ])
      .then((users) =>
        CalendarHubClient.getCalendarsByUserClientId(users.map((u) => u.id)),
      )
      .then((calendars) => {
        setStaffCalendars(calendars)
      })
  }, [])

  const { groupings, customFilters } = useMemo((): {
    groupings: CalendarGroupings
    customFilters: CalendarFilters | undefined
  } => {
    if (showAllPrograms) {
      return buildMsCalendarGroupingsAndFiltersForProgramOwner(
        allProgramsCalendars,
        programs,
      )
    }
    return {
      groupings: buildCalendarGroupings(classCalendars, staffCalendars),
      customFilters: undefined,
    }
  }, [
    showAllPrograms,
    allProgramsCalendars,
    programs,
    classCalendars,
    staffCalendars,
  ])

  const {
    msEvents,
    onChange,
    onDelete,
    addCalendarIdsToFetch,
    setFetchOrganized,
    loading,
    onCalendarDatesChange,
    onMergedCopySave,
  } = useEventActions([], dateRange, setDateRange)

  if (!user) {
    return null
  }
  return (
    <Stack
      sx={{
        width: "100%",
        height: "100%",
        bgColor: appTheme.palette.primary.contrast,
      }}
    >
      <Typography variant="h6" pl="24px" pb="8px">
        Calendar
      </Typography>
      {loading && <FetchEventSnackbar />}
      <Stack
        flex={1}
        overflow="auto"
        sx={{
          width: "100%",
          height: "100%",
        }}
      >
        <MsCalendar
          events={msEvents}
          calendars={groupings}
          lazySection={lazyPastPrograms}
          customCalendarFilters={customFilters}
          onMergedCopySave={onMergedCopySave}
          getCalendar={(c) => {
            return undefined
          }}
          onEventChange={onChange}
          userId={user.clientId}
          onDeleteEvent={onDelete}
          canJoinLiveSessions={false}
          showDraftEvents
          showOrganizedByMe
          menuAction={{
            label: showAllPrograms
              ? "Showing all programs"
              : "Invite across all programs",
            isActive: showAllPrograms,
            helperText: showAllPrograms
              ? "Tap to return to this course only"
              : "This calendar shows only this course",
            onClick: () => setShowAllPrograms((value) => !value),
          }}
          onSelectedCalendarChange={(calendarIds) => {
            setFetchOrganized(calendarIds.includes("organized"))
            const realCalendarIds = calendarIds.filter(
              (c) => !c.includes("draft") && !c.includes("organized"),
            )
            addCalendarIdsToFetch(realCalendarIds)
          }}
          supportCreateEventTypes={["live-session", "regular"]}
          onDatesRangeChange={onCalendarDatesChange}
        />
      </Stack>
    </Stack>
  )
}

const buildCalendarGroupings = (
  classCalendars: CourseClassCalendarDto[],
  staffCalendars: MsPrivateCalendarDto[],
): CalendarGroupings => {
  return [
    {
      title: "Classes",
      type: "single",
      groupingsType: "classes",
      groupingsAction: "all",
      calendars: classCalendars.map((c) => mapToMsCalendarProps(c)),
    },
    {
      title: "Staff",
      type: "single",
      groupingsType: "staff",
      groupingsAction: "invite",
      calendars: staffCalendars.map((c) => mapToMsCalendarProps(c)),
    },
  ]
}
